import * as R from 'ramda'
import * as RE from 'helpers/ramda-ext'
import { whenPackage, whenProduct } from 'domain/resources'
import { isActionType, isActionOneOfTypes } from 'helpers/ramda-ext/actions'
import { normalizePolyglotScope } from 'helpers/utils/customLabels'
import * as Actions from 'store/actions'
import {
  PayloadConfiguratorSelectionChangeAction,
  PayloadFetchPackageElementsErrorAction,
  PayloadPackageElementsFetchedAction,
  PayloadToggleCheapestPrice,
  ToggleCheapestPriceAction
} from 'store/actions/configurator.types'
import { RegisteredAction } from 'store/actions/index.types'
import { ResourceFetchedActionPayload } from 'store/actions/resources.types'
import { PayloadStockFetchedAction } from 'store/actions/stock.types'
import { State as AllState } from 'store/reducers/index.types'
import { CurrentSelection, PackageResource, ProductResource, Resources } from 'types/typescript'
import {
  CreateQueryString,
  DefineDefaultPackageElements,
  InitialState,
  MarkFetched,
  State
} from './resources.types'

const overCustomLabels = R.over(R.lensPath(['content', 'customLabels']))

const whenNotSale = R.when(R.compose(R.not, R.propEq('type', 'sale')))
const setSaleKey = R.assoc('sale')

const createQueryString: CreateQueryString = res => R.merge(res, {
  query: { key: res.key }
})

const defineDefaultPackageElements: DefineDefaultPackageElements = resource => {
  const ecomm = Object.assign({ packageElements: [] }, resource.ecomm)

  return ({
    ...resource,
    ecomm
  })
}

const completeResources = R.curry((saleKey, resource) => R.pipe(
  R.prop('resources'),
  R.map(R.pipe(
    whenNotSale(setSaleKey(saleKey)),
    whenProduct(R.pipe(
      overCustomLabels(
        RE.transformKeys(normalizePolyglotScope)
      )
    )),
    createQueryString
  ))
)(resource))

const markFetched: MarkFetched = (resourceKey) => R.over(
  R.lensProp(resourceKey),
  R.assoc('fetched', true)
)

const currentSelectionDefaultValues = (packageResource: PackageResource): PackageResource => {
  const currentSelection = packageResource.currentSelection as CurrentSelection

  return ({
    ...packageResource,
    currentSelection: {
      ...currentSelection,
      checkin: currentSelection?.checkin !== undefined ? currentSelection.checkin : null,
      departureCity: currentSelection?.departureCity !== undefined ? currentSelection.departureCity : null,
      nights: currentSelection?.nights !== undefined ? currentSelection.nights : null
    }
  })
}

const initialState: Readonly<InitialState> = {}

// TODO replace RegisteredAction by actual action type in reducer when actions will be typed

// eslint-disable-next-line @typescript-eslint/default-param-last
export default (state: State = initialState, action: RegisteredAction): State => R.cond([
  [
    isActionOneOfTypes([Actions.version.VERSION_CHANGED, Actions.application.SALE_CHANGES]),
    R.always({})
  ], [
    isActionType(Actions.resources.RESOURCE_FETCHED),
    (a: RegisteredAction) => {
      const { key, saleKey } = a.payload as ResourceFetchedActionPayload
      return R.pipe(
        R.path(['payload', 'response']),
        completeResources(saleKey),
        (resources: State): State => markFetched(key)(resources),
        R.map(whenPackage(defineDefaultPackageElements)),
        RE.mergeDeep(state)
      )(a)
    }
  ], [
    isActionOneOfTypes([Actions.stock.SALE_STOCK_FETCHED, Actions.stock.PRODUCT_STOCK_FETCHED]),
    R.pipe(
      (a: RegisteredAction) => {
        const { key, response } = a.payload as PayloadStockFetchedAction
        const currentResource: ProductResource['resources'] | PackageResource['resources'] = state[key]?.resources ?? []

        if (currentResource.length > 0) {
          const init: State = {}
          return currentResource.reduce((acc: State, resourceKey: string): State => {
            const indexOfElement = response.findIndex((key: string) => resourceKey === key)
            if (indexOfElement !== -1) {
              acc[resourceKey] = { ...state[resourceKey], outOfStock: true }
            } else {
              acc[resourceKey] = { ...state[resourceKey], outOfStock: false }
            }
            return acc
          }, init)
        }

        return response.reduce((acc, resourceKey) => {
          acc[resourceKey] = { outOfStock: true }
          return acc
        }, {})
      },
      RE.mergeDeep(state)
    )
  ], [
    isActionType(Actions.configurator.CONFIGURATOR_SELECTION_CHANGE),
    (a: RegisteredAction) => {
      const { packageKey, selection } = a.payload as PayloadConfiguratorSelectionChangeAction

      return R.pipe(
        R.ifElse(
          () => ((!!selection.departureCity || !!selection.nights) && !selection.checkin),
          R.over(
            R.lensPath([packageKey, 'currentSelection']),
            R.merge(R.__, {
              ...selection,
              checkin: !!selection.departureCity || !!selection.nights ? null : selection.checkin,
              nights: selection.departureCity ? null : selection.nights
            })
          ),
          R.over(
            R.lensPath([packageKey, 'currentSelection']),
            R.merge(R.__, selection)
          )
        ),
        R.when(
          () => (selection.departureCity !== undefined || selection.nights !== undefined),
          R.assocPath([packageKey, 'ecomm', 'packageElements'], [])
        ),
        R.over(
          R.lensProp(packageKey),
          currentSelectionDefaultValues
        )
      )(state)
    }
  ], [
    isActionType(Actions.configurator.TOGGLE_CHEAPEST_PRICE),
    (action: ToggleCheapestPriceAction) => {
      const { isToggled, packageKey } = action.payload as PayloadToggleCheapestPrice

      return ({
        ...state,
        [packageKey]: {
          ...state[packageKey],
          ecomm: {
            ...state[packageKey].ecomm,
            isCheapestPriceToggled: isToggled
          }
        }
      })
    }
  ], [
    isActionType(Actions.configurator.RESET_CHEAPEST_PRICE_TOGGLE),
    (action: ToggleCheapestPriceAction) => {
      const { packageKey } = action.payload as PayloadToggleCheapestPrice

      return ({
        ...state,
        [packageKey]: {
          ...state[packageKey],
          ecomm: {
            ...state[packageKey].ecomm,
            isCheapestPriceToggled: false
          }
        }
      })
    }
  ], [
    isActionType(Actions.configurator.ACTIVE_CHEAPEST_PRICE_TOGGLE),
    (action: ToggleCheapestPriceAction) => {
      const { packageKey } = action.payload as PayloadToggleCheapestPrice

      return ({
        ...state,
        [packageKey]: {
          ...state[packageKey],
          ecomm: {
            ...state[packageKey].ecomm,
            isCheapestPriceToggled: true
          }
        }
      })
    }
  ], [
    isActionType(Actions.configurator.PACKAGE_ELEMENTS_FETCHED),
    (a: RegisteredAction) => {
      const { packageKey, departureDates } = a.payload as PayloadPackageElementsFetchedAction

      return R.assocPath(
        [packageKey, 'ecomm', 'packageElements'],
        departureDates,
        state
      )
    }
  ], [
    isActionType(Actions.configurator.FETCH_PACKAGE_ELEMENTS_ERROR),
    (a: RegisteredAction) => {
      const { packageKey } = a.payload as PayloadFetchPackageElementsErrorAction

      return R.assocPath(
        [packageKey, 'hasPickersFetchedError'],
        true,
        state
      )
    }
  ], [
    isActionType(Actions.stock.PACKAGE_STOCK_FETCHED),
    a => R.ifElse(
      () => a.payload.response.length > 0,
      R.over(
        R.lensPath([a.payload.key, 'ecomm', 'packageElements']),
        R.map(packageElement => {
          const indexOfElement = a.payload.response.findIndex((packageElementKey) => packageElement.key === packageElementKey)

          if (indexOfElement !== -1) {
            return ({ ...packageElement, isAvailable: false })
          }

          return ({ ...packageElement, isAvailable: true })
        })
      ),
      R.always(state)
    )(state)
  ], [
    isActionType(Actions.stock.PICKERS_BINDINGS_STOCK_FETCHED),
    actionPickersBindingsStock => {
      if (actionPickersBindingsStock.payload.response.pickers?.length > 0) {
        return R.assocPath([actionPickersBindingsStock.payload.packageKey, 'ecomm', 'pickersBindingsStock'], actionPickersBindingsStock.payload.response.pickers, state)
      }
      return state
    }
  ], [
    isActionType(Actions.reviews.PRODUCT_REVIEWS_FETCHED),
    ({ payload: { productKey, response } }) => R.over(
      R.lensProp(productKey),
      R.assoc('reviews', response.ratingsSummary)
    )(state)
  ], [
    R.T,
    R.always(state)
  ]
])(action)

export const getResources: ((state: AllState) => Resources) = R.prop('resources')
